import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import InstructorSelector from "./InstructorSelector";

// Each instructor routes to their own dedicated form endpoint
const FORM_URLS: Record<string, string> = {
  nathaniel: "https://readdy.ai/api/form/d74s39avu4krcbn3bbc0",
  destiny:   "https://readdy.ai/api/form/d74s39avu4krcbn3bbcg",
};

const allCourses = [
  { label: "BLS Provider Class", price: "$85", cat: "Core" },
  { label: "BLS Renewal", price: "$75", cat: "Core" },
  { label: "BLS Blended (Online + Hands-On)", price: "$95", cat: "Core" },
  { label: "BLS Heartcode Skills Session", price: "$55", cat: "Core" },
  { label: "Heartsaver CPR / AED / First Aid", price: "$95–$105", cat: "Core" },
  { label: "ACLS Provider (Initial)", price: "$230", cat: "Advanced" },
  { label: "ACLS Renewal / Refresher", price: "$175", cat: "Advanced" },
  { label: "PALS Provider (Initial)", price: "$230", cat: "Advanced" },
  { label: "PALS Renewal / Refresher", price: "$175", cat: "Advanced" },
  { label: "ACLS + BLS Combo", price: "$250", cat: "Bundle" },
  { label: "ACLS + PALS + BLS Bundle", price: "$425", cat: "Bundle" },
  { label: "BLS Instructor Course", price: "$500", cat: "Instructor" },
  { label: "Group Training (Custom Quote)", price: "Custom", cat: "Group" },
];

const timeSlots = [
  "9:00 AM",
  "1:00 PM",
  "4:00 PM",
  "8:00 PM",
];

const sessionTypes = [
  { id: "individual", label: "Individual", icon: "ri-user-line" },
  { id: "group", label: "Group / Corporate", icon: "ri-group-line" },
];

export default function BookingForm() {
  // No default — student must actively choose
  const [instructor, setInstructor] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [sessionType, setSessionType] = useState("individual");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [instructorError, setInstructorError] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate instructor selection
    if (!instructor) {
      setInstructorError(true);
      const el = document.getElementById("instructor-selector");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setInstructorError(false);
    setSubmitting(true);

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) data.append(input.name, input.value);
    });
    data.append("session_type", sessionType);
    data.append("preferred_time", selectedTime);

    const instructorLabel =
      instructor === "destiny"
        ? "Destiny, RN (Lead Instructor)"
        : "Nathaniel, RN (CPR Instructor)";
    data.set("instructor", instructorLabel);

    const targetUrl = FORM_URLS[instructor];

    try {
      await fetch(targetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const instructorLabel =
      instructor === "destiny" ? "Destiny, RN" : "Nathaniel, RN";
    return (
      <div
        className="flex flex-col items-center justify-center text-center py-20 px-8 rounded-2xl h-full"
        style={{
          background: "linear-gradient(160deg, #1a0a2e 0%, #0f0d1a 100%)",
          border: "1px solid rgba(212,175,55,0.3)",
        }}
      >
        <div
          className="w-20 h-20 flex items-center justify-center rounded-full mb-6"
          style={{
            background:
              "linear-gradient(135deg, rgba(212,175,55,0.2), rgba(107,70,193,0.3))",
            border: "2px solid rgba(212,175,55,0.5)",
          }}
        >
          <i className="ri-check-double-line text-brand-gold text-3xl" />
        </div>
        <h3 className="font-serif text-3xl font-bold text-white mb-3">
          Request Sent!
        </h3>
        <p className="text-white/70 text-base leading-relaxed mb-3 max-w-sm font-semibold">
          Thank you. Your request has been sent to your selected instructor.
        </p>
        <p className="text-white/50 text-sm leading-relaxed mb-2 max-w-sm">
          <i className="ri-nurse-line mr-1 text-brand-gold/70" />
          Routed to <span className="text-brand-gold font-bold">{instructorLabel}</span>
        </p>
        <p className="text-white/45 text-sm leading-relaxed mb-8 max-w-sm">
          Please check your email for confirmation. Same-day certification available in
          Kissimmee, FL.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 font-bold text-sm px-6 py-3 rounded-lg cursor-pointer whitespace-nowrap"
            style={{
              background: "linear-gradient(135deg, #D4AF37, #b8952e)",
              color: "#1A1A1A",
            }}
          >
            <i className="ri-home-line" />
            Back to Home
          </Link>
          <button
            type="button"
            onClick={() => {
              setSubmitted(false);
              setInstructor("");
              setSelectedTime("");
              setSelectedCourse("");
            }}
            className="flex items-center gap-2 font-semibold text-sm px-6 py-3 rounded-lg cursor-pointer whitespace-nowrap"
            style={{
              border: "1px solid rgba(255,255,255,0.2)",
              color: "rgba(255,255,255,0.8)",
            }}
          >
            <i className="ri-add-line" />
            Book Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(160deg, #1a0a2e 0%, #0f0d1a 100%)",
        border: "1px solid rgba(212,175,55,0.3)",
      }}
    >
      {/* Form header */}
      <div
        className="px-7 py-5 flex items-center gap-3"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
      >
        <div
          className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0"
          style={{ background: "linear-gradient(135deg, #6B46C1, #4C1D95)" }}
        >
          <i className="ri-calendar-check-line text-brand-gold text-base" />
        </div>
        <div>
          <h3 className="text-white font-bold text-base leading-none">
            Reserve Your Spot
          </h3>
          <p className="text-white/40 text-xs mt-0.5">Fill in your details to book</p>
        </div>
        <div
          className="ml-auto flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold"
          style={{
            background: "rgba(212,175,55,0.12)",
            border: "1px solid rgba(212,175,55,0.3)",
            color: "#D4AF37",
          }}
        >
          <i className="ri-time-line" /> Same-Day Cert
        </div>
      </div>

      <form data-readdy-form onSubmit={handleSubmit} className="px-7 py-6 space-y-6">
        {/* Instructor Selection */}
        <div id="instructor-selector">
          <InstructorSelector
            selected={instructor}
            onChange={(val) => {
              setInstructor(val);
              setInstructorError(false);
            }}
          />
          {/* Hidden field carries the value for form data */}
          <input
            type="hidden"
            name="instructor"
            value={
              instructor === "destiny"
                ? "Destiny, RN (Lead Instructor)"
                : instructor === "nathaniel"
                ? "Nathaniel, RN (CPR Instructor)"
                : ""
            }
          />
          {instructorError && (
            <div
              className="mt-3 flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold"
              style={{
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.4)",
                color: "#fca5a5",
              }}
            >
              <i className="ri-error-warning-line text-base shrink-0" />
              Please select an instructor before submitting.
            </div>
          )}
        </div>

        <div style={{ height: "1px", background: "rgba(255,255,255,0.06)" }} />

        {/* Name + Email */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              required
              placeholder="Your full name"
              className="w-full h-11 px-4 rounded-lg text-sm text-white placeholder-white/25 outline-none transition-all duration-200 focus:ring-1"
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            />
          </div>
          <div>
            <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
              Email *
            </label>
            <input
              type="email"
              name="email"
              required
              placeholder="you@email.com"
              className="w-full h-11 px-4 rounded-lg text-sm text-white placeholder-white/25 outline-none transition-all duration-200"
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            />
          </div>
        </div>

        {/* Phone + Course */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
              Phone *
            </label>
            <input
              type="tel"
              name="phone"
              required
              placeholder="(555) 000-0000"
              className="w-full h-11 px-4 rounded-lg text-sm text-white placeholder-white/25 outline-none"
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            />
          </div>
          <div>
            <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
              Select Course *
            </label>
            <select
              name="course"
              required
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full h-11 px-4 rounded-lg text-sm outline-none cursor-pointer"
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: selectedCourse ? "white" : "rgba(255,255,255,0.3)",
              }}
            >
              <option value="" style={{ background: "#1a0a2e" }}>
                Choose a course...
              </option>
              {["Core", "Advanced", "Bundle", "Instructor", "Group"].map((cat) => (
                <optgroup
                  key={cat}
                  label={`── ${cat}`}
                  style={{ background: "#1a0a2e", color: "#D4AF37" }}
                >
                  {allCourses
                    .filter((c) => c.cat === cat)
                    .map((c) => (
                      <option
                        key={c.label}
                        value={c.label}
                        style={{ background: "#1a0a2e", color: "white" }}
                      >
                        {c.label} — {c.price}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
            Preferred Date *
          </label>
          <input
            type="date"
            name="preferred_date"
            required
            className="w-full h-11 px-4 rounded-lg text-sm text-white outline-none"
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              colorScheme: "dark",
            }}
          />
        </div>

        {/* Time Slots */}
        <div>
          <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
            Preferred Time *
          </label>
          <div className="grid grid-cols-4 gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => setSelectedTime(slot)}
                className="py-2.5 px-2 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={
                  selectedTime === slot
                    ? {
                        background: "linear-gradient(135deg, #6B46C1, #4C1D95)",
                        border: "1.5px solid rgba(212,175,55,0.6)",
                        color: "#fff",
                        boxShadow: "0 2px 12px rgba(107,70,193,0.35)",
                      }
                    : {
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.55)",
                      }
                }
              >
                {slot}
              </button>
            ))}
          </div>
          {!selectedTime && (
            <p className="text-white/30 text-xs mt-1.5">Please select a time slot</p>
          )}
        </div>

        {/* Session Type */}
        <div>
          <label className="block text-white/70 text-xs font-semibold mb-2 uppercase tracking-wide">
            Session Type
          </label>
          <div
            className="flex p-1 rounded-xl gap-1"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            {sessionTypes.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setSessionType(t.id)}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={
                  sessionType === t.id
                    ? {
                        background: "linear-gradient(135deg, #6B46C1, #4C1D95)",
                        color: "#fff",
                      }
                    : { color: "rgba(255,255,255,0.4)" }
                }
              >
                <i className={`${t.icon} text-sm`} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
            Notes / Questions
          </label>
          <textarea
            name="notes"
            rows={3}
            maxLength={500}
            placeholder="Group size, special requirements, questions..."
            className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/25 outline-none resize-none"
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          />
          <p className="text-white/25 text-xs mt-1">Max 500 characters</p>
        </div>

        {/* Routing indicator — shows who submission goes to */}
        {instructor && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-semibold"
            style={{
              background: "rgba(212,175,55,0.07)",
              border: "1px solid rgba(212,175,55,0.25)",
              color: "rgba(212,175,55,0.85)",
            }}
          >
            <i className="ri-send-plane-line text-sm shrink-0" />
            <span>
              This request will be sent to{" "}
              <span className="text-brand-gold font-bold">
                {instructor === "destiny" ? "Destiny, RN" : "Nathaniel, RN"}
              </span>{" "}
              — who will reach out to confirm your booking.
            </span>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !selectedTime || !instructor}
          className="w-full font-black text-base py-4 rounded-xl cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{
            background: "linear-gradient(135deg, #D4AF37, #b8952e)",
            color: "#1A1A1A",
            boxShadow: "0 4px 24px rgba(212,175,55,0.4)",
          }}
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> Submitting...
            </>
          ) : (
            <>
              <i className="ri-calendar-check-line" /> Reserve My Spot
            </>
          )}
        </button>

        {(!instructor || !selectedTime) && (
          <p className="text-center text-white/30 text-xs">
            {!instructor && !selectedTime
              ? "Select an instructor and time slot to continue"
              : !instructor
              ? "Please select an instructor to continue"
              : "Please select a time slot to continue"}
          </p>
        )}

        <p className="text-center text-white/30 text-xs">
          <i className="ri-lock-line mr-1 text-brand-gold/60" />
          Secure booking — we&apos;ll confirm within hours. Same-day classes available in
          Kissimmee, FL.
        </p>
      </form>
    </div>
  );
}
